// hooks
import { Fragment, useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useHttp from "hooks/use-http";

// routes
import { useParams, useNavigate } from "react-router-dom";

// material styles
import { Grid, Button, TextField } from "@mui/material";

// material icons
import CheckIcon from "@mui/icons-material/Check";

// react icons
import { MdDelete } from "react-icons/md";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import BackButton from "components/shared/BackButton";
import DeleteModal from "components/shared/Modal/Delete";

// helper
import Toast from "helper/toast";

// styled component
import {
  StyledStack,
  StyledTitle,
  ButtonContainer,
} from "./editSubCategory.style";

// request methods
import {
  deleteProductsSubCategoryByIdRequest,
  editProductsCategoryRequest,
  getProductCategoryByIdRequest,
} from "api/requests/products";

// validation
import { categorySchema, validateImg } from "validations";
import Upload from "components/shared/Upload";

export default function EditProductsSubCategory() {
  const { products_category_id, products_subcategory_id } = useParams();

  const navigate = useNavigate();

  const {
    request,
    loading,
    setLoading,
    isUploading,
    uploadProgress,
    setUploadProgress,
  } = useHttp(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(categorySchema),
  });

  // delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  //handle delete modal
  const handelShowModal = () => setShowDeleteModal((state) => !state);

  // delete products subcategory by id
  const handleDeleteItem = useCallback(
    async (id) => {
      setLoading(false);
      const res = await request({
        url: () => deleteProductsSubCategoryByIdRequest(id),
      });
      if (res) {
        Toast({ type: "success", title: "حذف زیردسته " });
        navigate(`/products/category/${products_category_id}/subcategory`);
      }
      handelShowModal();
    },
    [setLoading]
  );

  // subcategory image state
  const [subCategoryImg, setSubCategoryImg] = useState({
    url: null,
    isChanged: false,
  });

  // image handler
  const imageHandler = (val) => {
    validateImg(val);
    setSubCategoryImg({ url: val, isChanged: true });
  };

  // get subcategory by id data
  const getSubCategoryData = useCallback(async () => {
    setLoading(true);

    const res = await request({
      url: getProductCategoryByIdRequest,
      values: {
        id: products_subcategory_id,
      },
    });
    if (res) {
      if (res.data.image) {
        setSubCategoryImg({ url: res.data.image, isWeb: true });
      }
      reset({ title: res.data.title });
    }
  }, [setLoading]);

  // edit subcategory
  const onSubmit = async (data) => {
    const res = await request({
      url: () =>
        editProductsCategoryRequest(
          { ...data, id: products_subcategory_id, image: subCategoryImg },
          (progress) => setUploadProgress(progress)
        ),
      isFile: true,
    });

    if (res) {
      reset({
        title: "",
      });
      Toast({ type: "success", title: "ویرایش زیردسته " });
      navigate(`/products/category/${products_category_id}/subcategory`);
    }
  };

  useEffect(() => {
    getSubCategoryData();
  }, [getSubCategoryData]);

  return (
    <DrawerCM title="ویرایش زیردسته محصولات">
      {loading ? (
        <LoadingCM />
      ) : (
        <Fragment>
          <Grid container>
            <Grid item mt={3} xs={12}>
              <BackButton
                Route={`/products/category/${products_category_id}/subcategory`}
              />
            </Grid>
            <StyledStack>
              <Grid>
                <StyledTitle>ویرایش زیردسته</StyledTitle>
                <Grid container>
                  <Grid item xs={12} p={1.5}>
                    <TextField
                      label="عنوان زیردسته"
                      variant="outlined"
                      fullWidth
                      id="title"
                      {...register("title")}
                      error={Boolean(errors.title)}
                      helperText={errors.title?.message}
                    />
                  </Grid>

                  <Grid item md={6} xs={12} p={1.2}>
                    {subCategoryImg.url ? (
                      <Upload
                        isWeb={subCategoryImg.isWeb}
                        isLocal={!subCategoryImg.isWeb}
                        file={subCategoryImg.url}
                        refillable
                        type="add-image"
                        onChange={imageHandler}
                        width={"100%"}
                        height={"100%"}
                        objectFit="contain"
                      />
                    ) : (
                      <Upload
                        isLocal={subCategoryImg.isChanged}
                        file={subCategoryImg.url}
                        refillable
                        type="add-image"
                        onChange={imageHandler}
                        width={"100%"}
                        height={"100%"}
                        objectFit="contain"
                      />
                    )}
                  </Grid>
                  <ButtonContainer item xs={12}>
                    <Button
                      color="success"
                      startIcon={<CheckIcon />}
                      variant="contained"
                      sx={{ mt: 1, mr: 1 }}
                      onClick={handleSubmit(onSubmit)}
                    >
                      ثبت
                    </Button>
                    <Button
                      color="error"
                      endIcon={<MdDelete />}
                      variant="contained"
                      sx={{ mt: 1, mr: 1 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handelShowModal();
                      }}
                    >
                      حذف
                    </Button>
                  </ButtonContainer>
                </Grid>
              </Grid>
            </StyledStack>
          </Grid>
          <DeleteModal
            show={showDeleteModal}
            onClose={handelShowModal}
            onDelete={handleDeleteItem.bind(null, products_subcategory_id)}
          />
        </Fragment>
      )}
    </DrawerCM>
  );
}
